import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface CheckinState {
  id: string;
  garita: string;
  mode: string;
  lane_type: string;
  created_at: string;
  photo_url?: string;
  check_out_time?: string;
}

export const useCheckinState = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [currentCheckin, setCurrentCheckin] = useState<CheckinState | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for active check-in on mount
  const checkActiveCheckin = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      // Look for check-ins without check-out time
      const { data: checkins, error } = await supabase
        .from('checkins')
        .select(`
          id,
          garita,
          mode,
          lane_type,
          created_at,
          photo_url,
          check_out_time
        `)
        .eq('user_id', user.id)
        .is('check_out_time', null)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error checking active checkin:', error);
        return;
      }

      if (checkins && checkins.length > 0) {
        setCurrentCheckin(checkins[0] as unknown as CheckinState);
        
        // Update localStorage for UI consistency
        localStorage.setItem('isCheckedIn', 'true');
        localStorage.setItem('checkInTime', (checkins[0] as any).created_at);
        localStorage.setItem('checkInBorder', (checkins[0] as any).garita);
        localStorage.setItem('checkInMode', (checkins[0] as any).mode);
        localStorage.setItem('checkInLane', (checkins[0] as any).lane_type);
        localStorage.setItem('checkInId', (checkins[0] as any).id);
      } else {
        // Clear any stale localStorage data
        localStorage.removeItem('isCheckedIn');
        localStorage.removeItem('checkInTime');
        localStorage.removeItem('checkInBorder');
        localStorage.removeItem('checkInMode');
        localStorage.removeItem('checkInLane');
        localStorage.removeItem('checkInId');
      }
    } catch (error) {
      console.error('Error checking active checkin:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Create a new check-in with geofence detection
  const createCheckin = async (data: {
    garita: string;
    mode: string;
    lane_type: string;
    notes?: string;
    photo_url?: string;
    position?: { latitude: number; longitude: number; accuracy?: number };
    geofence_match?: string;
    detected_lane_type?: string;
    crossing_mode?: string;
    quality_score?: number;
    gps_accuracy_meters?: number;
    verified_by_photo?: boolean;
    geofence_match_bool?: boolean;
  }) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    const checkinData = {
      user_id: user.id,
      garita: data.garita,
      mode: data.mode,
      lane_type: data.lane_type,
      notes: data.notes || null,
      photo_url: data.photo_url || null,
      photo_uploaded: !!data.photo_url,
      ai_analysis_completed: false,
      gps_latitude: data.position?.latitude || null,
      gps_longitude: data.position?.longitude || null,
      geofence_match: data.geofence_match || null,
      detected_lane_type: data.detected_lane_type || null,
      crossing_mode: data.crossing_mode || null,
      unknown_geofence: !data.geofence_match && !!data.position,
      quality_score: data.quality_score || 0.5,
      gps_accuracy_meters: data.gps_accuracy_meters || data.position?.accuracy || null,
      verified_by_photo: data.verified_by_photo || false,
      geofence_match_bool: data.geofence_match_bool || false,
      created_at: new Date().toISOString()
    };

    const { data: checkin, error } = await supabase
      .from('checkins')
      .insert(checkinData as any) // Type assertion until Supabase types are regenerated
      .select()
      .single();

    if (error) {
      // Enhanced error logging for debugging
      console.error('Check-in database error:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      
      // Throw error with more context
      const enhancedError = new Error(
        error.message || 'Failed to create check-in'
      );
      (enhancedError as any).originalError = error;
      throw enhancedError;
    }

    setCurrentCheckin(checkin);
    
    // Update localStorage
    localStorage.setItem('isCheckedIn', 'true');
    localStorage.setItem('checkInTime', checkin.created_at);
    localStorage.setItem('checkInBorder', checkin.garita);
    localStorage.setItem('checkInMode', checkin.mode);
    localStorage.setItem('checkInLane', checkin.lane_type);
    localStorage.setItem('checkInId', checkin.id);

    return checkin;
  };

  // Complete check-out using UPDATE method on checkins table
  const completeCheckout = async (showRewardedAd: boolean = false) => {
    if (!currentCheckin || !user) {
      throw new Error('No active check-in found');
    }

    const now = new Date();
    const checkinTime = new Date(currentCheckin.created_at);
    const waitTimeMinutes = Math.floor((now.getTime() - checkinTime.getTime()) / (1000 * 60));

    // Update the checkin record with check-out time
    const { error } = await supabase
      .from('checkins')
      .update({
        check_out_time: now.toISOString()
      } as any) // Type assertion until database types are updated
      .eq('id', currentCheckin.id)
      .eq('user_id', user.id); // Extra security check

    if (error) {
      throw error;
    }

    // Clear state
    setCurrentCheckin(null);
    
    // Clear localStorage
    localStorage.removeItem('isCheckedIn');
    localStorage.removeItem('checkInTime');
    localStorage.removeItem('checkInBorder');
    localStorage.removeItem('checkInMode');
    localStorage.removeItem('checkInLane');
    localStorage.removeItem('checkInId');

    // Signal to show rewarded ad if requested
    if (showRewardedAd) {
      window.dispatchEvent(new CustomEvent('show-checkout-ad'));
    }

    return waitTimeMinutes;
  };

  // Set up real-time subscription for checkin updates to refresh wait times
  useEffect(() => {
    const channel = supabase
      .channel('checkin-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'checkins'
        },
        (payload) => {
          console.log('Checkin updated (checkout):', payload);
          // Trigger wait time refresh in other hooks
          window.dispatchEvent(new CustomEvent('checkout-submitted'));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Check for active checkin on mount
  useEffect(() => {
    checkActiveCheckin();
  }, [checkActiveCheckin]);

  return {
    currentCheckin,
    isLoading,
    createCheckin,
    completeCheckout,
    refreshCheckinState: checkActiveCheckin
  };
};